import {
  CLUSTERS,
  getClusterById,
  getBookingModeForTier,
  normalizeGarageTier,
  tierMeetsFloor,
} from '../constants/garageServiceClusters';
import {
  getPackageServicesForTier,
  isPackageEligibleTier,
  isPackageServiceId,
} from '../constants/garagePackageServices';
import { expandClusterServices } from './garageServiceExpansion';
import { parseGarageServicesFromDetails } from '../constants/garageServices';

function slugifyLabel(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

function clusterServiceId(clusterId, label) {
  return `c${clusterId}_${slugifyLabel(label)}`;
}

function resolveGarageTierFromDetails(garageDetails = {}) {
  return (
    normalizeGarageTier(garageDetails.garageTier) ||
    normalizeGarageTier(garageDetails.sellerCategory) ||
    normalizeGarageTier(garageDetails.companyType) ||
    ''
  );
}

function resolveSelectedClusters(garageDetails = {}) {
  const raw = garageDetails.selectedClusters;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw
    .map(Number)
    .filter((n) => n >= 1 && n <= 10 && !seen.has(n) && (seen.add(n) || true))
    .sort((a, b) => a - b);
}

function buildClusterServiceLines(tier, clusterId, options = {}) {
  const cluster = getClusterById(clusterId);
  if (!cluster || !tierMeetsFloor(tier, cluster.tierFloor)) return [];

  const bookingMode = getBookingModeForTier(tier);
  const names = expandClusterServices(tier, clusterId, options);

  return names.map((label) => ({
    serviceId: clusterServiceId(clusterId, label),
    label,
    name: label,
    clusterId,
    clusterLabel: cluster.label,
    tierFloor: cluster.tierFloor,
    fuelGate: cluster.fuelGate,
    bookingMode,
    packageEligible: false,
    legacy: false,
  }));
}

function buildPackageServiceLines(tier) {
  const canonical = normalizeGarageTier(tier);
  if (!isPackageEligibleTier(canonical)) return [];

  const bookingMode = getBookingModeForTier(canonical);
  return getPackageServicesForTier(canonical).map((pkg) => ({
    serviceId: pkg.serviceId,
    label: pkg.label,
    name: pkg.label,
    clusterId: null,
    clusterLabel: 'Maintenance Packages',
    tierFloor: 'medium',
    fuelGate: 'all',
    bookingMode,
    packageEligible: true,
    legacy: false,
    largeOnly: !!pkg.largeOnly,
  }));
}

export function getFullCatalogServiceLines(tier = 'large') {
  const canonical = normalizeGarageTier(tier) || 'large';
  const seen = new Set();
  const out = [];

  const push = (line) => {
    if (!line?.serviceId || seen.has(line.serviceId)) return;
    seen.add(line.serviceId);
    out.push(line);
  };

  for (const cluster of CLUSTERS) {
    if (!tierMeetsFloor(canonical, cluster.tierFloor)) continue;
    for (const line of buildClusterServiceLines(canonical, cluster.id, {})) {
      push(line);
    }
  }

  for (const line of buildPackageServiceLines(canonical)) {
    push(line);
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export function buildServiceLinesForGarage(garageDetails = {}, options = {}) {
  const tier = resolveGarageTierFromDetails(garageDetails);
  const clusterIds = resolveSelectedClusters(garageDetails);
  const fuelSpecialistTypes = Array.isArray(garageDetails.fuelSpecialistTypes)
    ? garageDetails.fuelSpecialistTypes
    : [];
  const seen = new Set();
  const out = [];

  const push = (line) => {
    if (!line?.serviceId || seen.has(line.serviceId)) return;
    seen.add(line.serviceId);
    out.push(line);
  };

  if (clusterIds.length && tier) {
    for (const clusterId of clusterIds) {
      for (const line of buildClusterServiceLines(tier, clusterId, { fuelSpecialistTypes })) {
        push(line);
      }
    }
  }

  if (options.includePackages !== false && tier) {
    for (const line of buildPackageServiceLines(tier)) {
      push(line);
    }
  }

  if (options.includeLegacy !== false) {
    for (const label of parseGarageServicesFromDetails(garageDetails)) {
      const key = label.toLowerCase();
      if (out.some((row) => row.label.toLowerCase() === key)) continue;
      push({
        serviceId: `legacy_${slugifyLabel(label)}`,
        label,
        name: label,
        clusterId: null,
        clusterLabel: null,
        tierFloor: tier || 'small',
        fuelGate: 'all',
        bookingMode: getBookingModeForTier(tier) || 'instant',
        packageEligible: false,
        legacy: true,
      });
    }
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export function getGarageDealServiceOptions(garageDetails = {}) {
  return buildServiceLinesForGarage(garageDetails, { includeLegacy: true });
}

export function findServiceLineById(lines, serviceId) {
  const id = String(serviceId || '').trim();
  if (!id) return null;
  return (lines || []).find((row) => row.serviceId === id) || null;
}

export function isPackageDealBlockedForTier(tier, serviceId) {
  const canonical = normalizeGarageTier(tier);
  if (!isPackageServiceId(serviceId)) return false;
  if (!isPackageEligibleTier(canonical)) return true;
  if (serviceId === 'fleet_maintenance' && canonical !== 'large') return true;
  return false;
}

export function groupServiceLinesByCluster(lines = []) {
  const groups = new Map();
  for (const line of lines) {
    const key = line.clusterLabel || 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(line);
  }
  return [...groups.entries()].map(([label, items]) => ({ label, items }));
}
